import type { Cell, CellContext, ColumnMeta, Row, Table } from '@tanstack/react-table';
import { ClickAwayListener, Box } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState, memo, useMemo } from 'react';
import styled from '@emotion/styled';
import { PencilSimple, Trash } from '@phosphor-icons/react';
import { AccessControl } from 'features/accessControl';
import { DotsMenuButton } from 'features/misc/table/components/DotsMenuButton';
import { isCellEditable } from 'features/misc/table/utils';
import { useEscapeToClose } from '../../../hooks';
import { useEditableContextOptional } from '../../OptimizedTableProvider';
import { TableTooltip } from '../../TableTooltip';

export type SharedEditableCellProps<T extends { id: number | string }> = Pick<
  CellContext<T, unknown>,
  'getValue' | 'column'
> & {
  row?: Row<T>;
  table?: Table<T>;
  children: React.ReactNode;
  meta?: ColumnMeta<T, unknown>;
  readModeEl?: React.ReactNode;
  error?: boolean;
  isEditable?: boolean;
  validateRow?: boolean;
  isSupplierEditable?: boolean;
  tooltipTitle?: React.ReactNode;
  editableInNewRow?: boolean;
  hideActions?: boolean;
  editorHeight?: string;
  cell?: Cell<T, unknown>;
  /** Called when user presses Escape or clicks away (optional when using context) */
  onClearEdit?: () => void;
};

const Editor = styled.div<{ height?: string }>`
  width: 100%;
  height: ${({ height = '100%' }) => height};
`;

const Value = styled.span`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChildrenContainer = styled('div')<{ error?: boolean; isEditable?: boolean }>`
  height: 100%;
  display: flex;
  align-items: center;
  cursor: ${p => (p.isEditable ? 'pointer' : 'auto')};
  .MuiAutocomplete-popper {
    min-width: 300px;
  }
  .MuiAutocomplete-root {
    width: 100%;
  }
  .MuiAutocomplete-endAdornment {
    display: none;
  }
  .MuiInput-root {
    padding-right: 0 !important;
  }
  ${props => (props.error ? 'border: 1px solid red;' : '')}
`;

const Container = ({
  children,
  tooltipTitle,
  error,
  ...rest
}: Omit<React.HTMLProps<HTMLDivElement>, 'as'> & {
  error?: React.ReactNode;
  tooltipTitle?: React.ReactNode;
  isEditable?: boolean;
}) => {
  return tooltipTitle ? (
    <TableTooltip title={tooltipTitle}>
      <ChildrenContainer {...rest}>{children}</ChildrenContainer>
    </TableTooltip>
  ) : (
    <ChildrenContainer {...rest}>{children}</ChildrenContainer>
  );
};

const DotsMenuButtonWrapper = styled(Box)`
  max-width: 18px;
`;

/**
 * Shared editable cell used by table field types and ChipCell.
 * Lives in shared so we can change behavior (Escape, focus, validation) without
 * being restricted by the legacy components/EditableCell.
 * Uses document capture-phase listener for Escape so it runs before inputs/portals.
 */
const SharedEditableCellInner = <T extends { id: number | string }>({
  getValue,
  row,
  table,
  meta,
  column,
  children,
  readModeEl,
  error,
  isEditable: isEditableProp,
  validateRow = true,
  isSupplierEditable,
  tooltipTitle,
  editableInNewRow = false,
  hideActions,
  editorHeight = '100%',
  cell,
  onClearEdit: onClearEditProp,
}: SharedEditableCellProps<T>) => {
  const editableCtx = useEditableContextOptional();
  const editableState = editableCtx?.editableState ?? null;
  const setEditableState = editableCtx?.setEditableState ?? ((): void => undefined);

  const isEditable =
    isEditableProp ??
    (editableState != null &&
      row != null &&
      column != null &&
      row.index === editableState.rowIndex &&
      column.id === editableState.columnId);
  const onClearEdit =
    onClearEditProp ?? (() => setEditableState({ rowIndex: null, columnId: null }));

  const initialValue = getValue() as string;
  const [value, setValue] = useState<string>(initialValue);
  const [editing, setEditing] = useState(false);
  const isUserEditingRef = useRef(false);

  useEffect(() => {
    if (!isUserEditingRef.current) {
      setValue(initialValue);
      setEditing(false);
    }
  }, [initialValue]);

  // Stabilize handleSetEditing with useCallback
  const handleSetEditing = useCallback(
    (toggle: boolean) => {
      if (toggle === false) {
        isUserEditingRef.current = false;
        setEditing(false);
        return;
      }
      if (toggle) {
        if (row?.original.id === -1 && editableInNewRow) {
          isUserEditingRef.current = true;
          setEditing(true);
          return;
        }
        if (row?.original.id !== -1) {
          isUserEditingRef.current = true;
          setEditing(true);
          return;
        }
      }
    },
    [row?.original.id, editableInNewRow]
  );

  useEffect(() => {
    if (isEditable) {
      handleSetEditing(true);
    } else {
      isUserEditingRef.current = false;
      setEditing(false);
    }
  }, [isEditable, handleSetEditing]);

  const handleEscape = useCallback(() => {
    onClearEdit?.();
    handleSetEditing(false);
  }, [onClearEdit, handleSetEditing]);

  useEscapeToClose(editing, handleEscape);

  const childOnKeyDown = (
    children as React.ReactElement & { props?: { onKeyDown?: (e: React.KeyboardEvent) => void } }
  )?.props?.onKeyDown;

  const handleClickAway = useCallback(() => {
    if (!error && validateRow) {
      onClearEdit?.();
      handleSetEditing(false);
    }
  }, [error, validateRow, onClearEdit, handleSetEditing]);

  const handleContainerClick = useCallback(() => {
    if (editableCtx?.setEditableState && row != null && column != null) {
      editableCtx.setEditableState({ rowIndex: row.index, columnId: column.id });
    }
    handleSetEditing(true);
  }, [editableCtx?.setEditableState, row, column, handleSetEditing]);

  const isEditableCell = isCellEditable(meta?.editable, cell);

  // Memoize the dots menu options to prevent recreation
  const menuOptions = useMemo(
    () => [
      {
        label: 'Edit',
        icon: <PencilSimple data-testid="pencil_simple_icon" size={16} />,
        onClick: () => handleSetEditing(true),
      },
      ...(meta?.onCellDelete
        ? [
            {
              label: 'Delete',
              icon: <Trash data-testid="trash_icon" size={16} />,
              onClick: () => {
                if (meta?.onCellDelete) {
                  meta.onCellDelete(table?.options.meta, row?.index ?? 0);
                }
              },
            },
          ]
        : []),
    ],
    [handleSetEditing, meta, table?.options.meta, row?.index]
  );

  if (!isEditableCell) {
    return (
      <Container isEditable={isEditableCell} tooltipTitle={tooltipTitle}>
        {readModeEl ?? value}
      </Container>
    );
  }

  const showDotsMenu = isEditableCell && !hideActions && row?.original.id !== -1;

  const editorContent = (
    <>
      <Container
        isEditable={isEditableCell}
        tooltipTitle={editing ? '' : tooltipTitle}
        onClick={handleContainerClick}
        error={error}
      >
        {editing ? (
          <ClickAwayListener onClickAway={handleClickAway}>
            <Editor height={editorHeight}>
              {React.isValidElement(children) &&
              'props' in children &&
              typeof children.props === 'object'
                ? React.cloneElement(children as React.ReactElement, { onKeyDown: childOnKeyDown })
                : children}
            </Editor>
          </ClickAwayListener>
        ) : readModeEl ? (
          readModeEl
        ) : (
          <Value>{value}</Value>
        )}
      </Container>
      {showDotsMenu ? (
        <DotsMenuButtonWrapper>
          <DotsMenuButton menuOptions={menuOptions} />
        </DotsMenuButtonWrapper>
      ) : null}
    </>
  );

  if (isSupplierEditable) {
    return editorContent;
  }

  return (
    <AccessControl
      write
      fallback={
        <Container isEditable={isEditableCell} tooltipTitle={editing ? '' : tooltipTitle}>
          {readModeEl ?? value}
        </Container>
      }
    >
      {editorContent}
    </AccessControl>
  );
};

// Wrap with memo for performance optimization
export const SharedEditableCell = memo(SharedEditableCellInner) as <
  T extends { id: number | string },
>(
  props: SharedEditableCellProps<T>
) => JSX.Element;
